import React, { useState, useEffect } from 'react';

import { Divider, Paper, TextField } from '@material-ui/core';
import { reloadStatsState, reloadFeedbackList } from 'actions/continuousFeedback';
import EmployeeSearch, { SingleValue } from 'components/EmployeeSearch';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import GoalParsedSingleValue from 'screens/performanceReview/components/ContinuousFeedback/common/GoalParsedSingleOption';
import GoalParserOption from 'screens/performanceReview/components/ContinuousFeedback/common/GoalParserOption';
import SelectVisibility from 'screens/performanceReview/components/ContinuousFeedback/common/SelectVisibility';
import styled from 'styled-components';
import { Button } from 'styles/admin';
import { StyledTypography, GutterCSS } from 'styles/common';

import {
  FEEDBACK_FORM_VISIBILITY,
  FEEDBACK_FORM_TYPE,
  CONTINUOUS_FEEDBACK_RELOAD_TYPE,
  FEEDBACK_COMMENT_STRINGIFIED_RAW_STATE,
  CONTINUOUS_FEEDBACK_FORM_STATUS,
  ALERT_MESSAGES,
} from 'constants/ProjectConstants';

import { requestNewFeedback } from 'services/continuousFeedback';
import { getEditorStateFromStringifiedRawState } from 'services/helpers/post';
import { showErrorSnackbar } from 'services/snackbar';
import { getTeamTasksForListView } from 'services/task';

const StyledPaper = styled(Paper)`
  display: flex;
  flex-direction: column;
`;

const SectionContainer = styled.div`
  margin: 0.5rem 0 1.5rem 0;
  ${GutterCSS}
`;

const ButtonContainer = styled.div`
  border-top: 1px solid #eaeaea;
  display: flex;
  padding: 1rem 0;
  min-height: 3rem;
  height: 8vh;
  justify-content: flex-end;
`;
const StyledButton = styled(Button)`
  height: 5ch;
  margin: 0 1rem;
`;
const makeGoalsAsSelectOptions = ({ goals }) =>
  goals.map((goal) => ({
    label: goal.text,
    value: goal.id,
    macros: goal.macros,
    goalObject: goal,
  }));

const MsRequestFeedback = ({
  loggedInEmployee,
  showFeedback = false,
  isNew = true,
  sender,
  requestFeedbackDetails = {},
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedFeedbackFromEmployee, setSelectedFeedbackFromEmployee] = useState(sender);
  const [selectedFeedbackForEmployee, setSelectedFeedbackForEmployee] = useState(loggedInEmployee);
  const [validForm, setValidForm] = useState(false);
  const [editorState, setEditorState] = useState(
    getEditorStateFromStringifiedRawState({
      FEEDBACK_COMMENT_STRINGIFIED_RAW_STATE,
    })
  );
  const [questionText, setQuestionText] = useState('');
  const [goalsList, setGoalsList] = useState([]);
  const [selectedFormVisibility, setSelectedFormVisibility] = useState(FEEDBACK_FORM_VISIBILITY[0]);
  const dispatch = useDispatch();
  const [selectedGoal, setSelectedGoal] = useState(null);
  const { review, employee } = requestFeedbackDetails;
  const handleResetData = ({ reloadType }) => {
    dispatch(reloadFeedbackList({ reloadType }));
  };

  const handleReloadStats = () => {
    dispatch(reloadStatsState());
  };

  const getGoalList = () => {
    const tagId = -6;
    const tagType = 'TEAM';
    const type = 'GOAL';
    const filters = [
      {
        type: 'NAME',
        value: selectedFeedbackForEmployee.value,
        operator: 'IN',
      },
    ];
    setLoading(true);
    getTeamTasksForListView({
      tagId,
      tagType,
      offset: 0,
      filters,
      type,
      limit: 100,
      startTS: 0,
      endTS: Date.now(),
    })
      .then((res) => {
        const goals = makeGoalsAsSelectOptions({ goals: res });
        setGoalsList(goals);
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(ALERT_MESSAGES.GENERIC.FAILURE));
      })
      .finally(() => setLoading(false));
  };

  const handleFeedbackForEmployeeChange = (option) => {
    setSelectedFeedbackForEmployee(option);
    setSelectedGoal(null);
  };

  const handleFeedbackFromEmployeeChange = (option) => {
    setSelectedFeedbackFromEmployee(option);
  };
  const handleFormVisibilityChange = (option) => {
    setSelectedFormVisibility(option);
  };

  const handleQuestionTextChange = (event) => {
    const { value } = event.target;
    setQuestionText(value);
  };
  const handleSelectedGoalChange = (option) => {
    setSelectedGoal(option);
  };
  const handleClearForm = () => {
    setQuestionText('');
    setSelectedGoal(null);
    setEditorState(
      getEditorStateFromStringifiedRawState({
        FEEDBACK_COMMENT_STRINGIFIED_RAW_STATE,
      })
    );

    onCancel();
  };

  const handleRequestFeedback = () => {
    setLoading(true);
    let body = {
      feedbackType: FEEDBACK_FORM_TYPE.REQUESTED,
      visibility: selectedFormVisibility.value,
      receiverEmployeeId: selectedFeedbackForEmployee.value,
      requesterEmployeeId: null,
      // providerEmployeeId: selectedFeedbackFromEmployee.value,
      providerEmployeeId: '',
      questionText,
    };
    if (selectedGoal?.value) {
      body = {
        ...body,
        goal: selectedGoal.goalObject,
      };
    }
    requestNewFeedback({ body, providerIds: [selectedFeedbackFromEmployee.value] })
      .then((res) => {
        if (selectedFeedbackForEmployee.value.toString() === selectedFeedbackFromEmployee.id) {
          handleResetData({ reloadType: CONTINUOUS_FEEDBACK_RELOAD_TYPE.SHARED_OWN });
        }
        handleReloadStats();
        handleClearForm();
      })
      .catch((err) => {
        console.error(err);
        dispatch(showErrorSnackbar(ALERT_MESSAGES.CONTINUOUS_FEEDBACK.REQUEST.FAILURE));
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const getPrimaryActionStatus = () => {
    if (isNew) {
      return true;
    }
    if (
      !review?.isArchived &&
      loggedInEmployee?.id === review?.receiverEmployeeId.toString() &&
      review.submissionStatus === CONTINUOUS_FEEDBACK_FORM_STATUS.COMPLETED
    ) {
      return true;
    }
    if (review?.isArchived && loggedInEmployee?.id === review?.receiverEmployeeId.toString()) {
      return true;
    }
    return false;
  };
  useEffect(() => {
    if (selectedFeedbackForEmployee && selectedFeedbackForEmployee?.value) {
      getGoalList();
    }
  }, [selectedFeedbackForEmployee]);

  useEffect(() => {
    if (selectedFeedbackFromEmployee?.value && selectedFeedbackForEmployee?.value && questionText.length > 0) {
      setValidForm(true);
    } else {
      setValidForm(false);
    }
  }, [questionText, selectedFeedbackForEmployee, selectedFeedbackFromEmployee]);

  return (
    <>
      <SectionContainer>
        <StyledTypography variant="body2" bottomGutter={1}>
          Request feedback from*
        </StyledTypography>
        {selectedFeedbackFromEmployee ? (
          <EmployeeSearch
            defaultValue={selectedFeedbackFromEmployee}
            customCSS={{ border: 'default', paddingLeft: 'default', borderRadius: 'default' }}
            displayName="Full Name"
            onChange={handleFeedbackFromEmployeeChange}
            components={{
              SingleValue,
            }}
          />
        ) : (
          <EmployeeSearch
            customCSS={{ border: 'default', paddingLeft: 'default', borderRadius: 'default' }}
            displayName="Full Name"
            onChange={handleFeedbackFromEmployeeChange}
            components={{
              SingleValue,
            }}
          />
        )}
      </SectionContainer>
      <SectionContainer>
        <StyledTypography variant="body2" bottomGutter={1}>
          Feedback for*
        </StyledTypography>
        {selectedFeedbackForEmployee ? (
          <EmployeeSearch
            defaultValue={selectedFeedbackForEmployee}
            customCSS={{ border: 'default', paddingLeft: 'default', borderRadius: 'default' }}
            displayName="Full Name"
            onChange={handleFeedbackForEmployeeChange}
            components={{
              SingleValue,
            }}
          />
        ) : (
          <EmployeeSearch
            customCSS={{ border: 'default', paddingLeft: 'default', borderRadius: 'default' }}
            displayName="Full Name"
            onChange={handleFeedbackForEmployeeChange}
            components={{
              SingleValue,
            }}
          />
        )}
      </SectionContainer>
      <SectionContainer>
        <StyledTypography variant="body2" bottomGutter={1}>
          Feedback visibility*
        </StyledTypography>
        <SelectVisibility value={selectedFormVisibility} onChange={handleFormVisibilityChange} />
      </SectionContainer>

      <SectionContainer bottomGutter={2}>
        <StyledTypography variant="body2" bottomGutter={1}>
          What do you want feedback on?*
        </StyledTypography>
        <Select
          placeholder="Add goal"
          options={goalsList}
          onChange={handleSelectedGoalChange}
          components={{ Option: GoalParserOption, SingleValue: GoalParsedSingleValue }}
          value={selectedGoal || null}
        />
      </SectionContainer>
      <SectionContainer topGutter={1}>
        <TextField
          id="feedback"
          rows={5}
          placeholder="Share request details*"
          multiline
          variant="outlined"
          fullWidth
          required
          value={questionText}
          onChange={handleQuestionTextChange}
        />
      </SectionContainer>
      <Divider />
      <ButtonContainer topGutter={3}>
        <StyledButton blueOutline size="large" leftGutter={3} onClick={handleClearForm}>
          Cancel
        </StyledButton>
        {getPrimaryActionStatus() && (
          <StyledButton
            blue
            size="large"
            leftGutter={3}
            onClick={handleRequestFeedback}
            disabled={loading || (showFeedback ? !!review?.isArchived : !validForm)}
          >
            Request
          </StyledButton>
        )}
      </ButtonContainer>
    </>
  );
};

export default MsRequestFeedback;
